import { CSSResultGroup, LitElement, PropertyValues, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { drawerStyles } from './drawer.styles';
import '../icon/icon.element';

/** Drawer positions */
export type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';
/** Drawer size variants */
export type DrawerVariant = 'basic' | 'small' | 'flexible';

/** Utility: focusable selector */
const FOCUSABLE = [
  'a[href]','button:not([disabled])','textarea:not([disabled])','input:not([disabled])','select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

@customElement('cds-drawer')
export class DrawerElement extends LitElement {
  static styles: CSSResultGroup = [drawerStyles];

  /** Whether drawer is open */
  @property({ type: Boolean, reflect: true }) open = false;
  /** Edge it appears from */
  @property({ type: String, reflect: true }) position: DrawerPosition = 'right';
  /** Size variant */
  @property({ type: String, reflect: true }) variant: DrawerVariant = 'basic';
  /** Show backdrop overlay */
  @property({ type: Boolean, reflect: true }) overlay = true;
  /** Close when backdrop clicked */
  @property({ type: Boolean, attribute: 'close-on-overlay-click' }) closeOnOverlayClick = true;
  /** Close when ESC pressed */
  @property({ type: Boolean, attribute: 'close-on-esc' }) closeOnEsc = true;
  /** Custom width for horizontal drawers */
  @property({ attribute: 'width' }) width?: string | number;
  /** Custom height for vertical drawers */
  @property({ attribute: 'height' }) height?: string | number;
  /** aria-label for panel */
  // Use string|null (not undefined) to align with base HTMLElement aria reflection expectations
  @property({ type: String, attribute: 'aria-label' }) ariaLabel: string | null = null;
  /** aria-labelledby for panel */
  @property({ type: String, attribute: 'aria-labelledby' }) ariaLabelledBy: string | null = null;

  /** Internal: track if opened once (enable transitions) */
  private openedOnce = false;
  /** Previously focused element to restore on close */
  private previouslyFocused?: HTMLElement | null;
  /** Track timeouts for cleanup */
  private timeouts: number[] = [];
  /** Panel element */
  @query('#panel') private panelEl?: HTMLElement | null;
  /** Footer slot element */
  @query('slot[name="footer"]') private footerSlot?: HTMLSlotElement | null;
  /** Whether footer has any projected content */
  @state() private hasFooterContent = false;

  connectedCallback(): void {
    super.connectedCallback();
    this._boundOnKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this._boundOnKeydown, true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._boundOnKeydown) {
      document.removeEventListener('keydown', this._boundOnKeydown, true);
    }
    this._clearTimeouts();
    this._unlockBodyScroll();
  }

  /** Public: programmatically open */
  openDrawer() { if (!this.open) { this._emitOpenChange(true); } }
  /** Public: programmatically close */
  closeDrawer() { if (this.open) { this._emitOpenChange(false); } }
  /** Public: toggle state */
  toggle() { this.open ? this.closeDrawer() : this.openDrawer(); }

  protected willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('open')) {
      if (this.open) {
        this._afterOpen();
      } else if (changed.get('open') !== undefined) { // avoid initial false run
        this._beforeClose();
      }
    }
  }

  protected firstUpdated(): void {
    this.hasFooterContent = this._evaluateFooterSlot();
  }

  protected updated(changed: PropertyValues<this>): void {
    if (changed.has('open')) {
      if (this.open) {
        // focus trap activation next frame for painted content
        requestAnimationFrame(() => this._autoFocus());
      } else {
        this._afterClosed();
      }
    }
  }

  render() {
    const rootClasses = {
      'akam-drawer': true,
      'is-open': this.open,
      'enable-transitions': this.openedOnce,
    };
    const panelClasses = [
      'akam-drawer__panel',
      `position-${this.position}`,
      `variant-${this.variant}`,
    ].join(' ');

    return html`
      <div class=${Object.keys(rootClasses).filter(k=> (rootClasses as any)[k]).join(' ')} aria-hidden=${this.open ? 'false':'true'}>
        ${this.overlay ? html`<div class="akam-drawer__backdrop ${this.open? 'is-open':''}" @click=${this.onBackdropClick} aria-hidden="true"></div>`: ''}
        <aside
          id="panel"
          class=${panelClasses}
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          aria-label=${this.ariaLabel || ''}
          aria-labelledby=${this.ariaLabelledBy || ''}
          style=${this._inlineStyle()}
        >
          <div class="akam-drawer__content">
            <header class="akam-drawer__header">
              <div class="akam-drawer__header-content"><slot name="header"></slot></div>
              <div class="akam-drawer__header-actions">
                <button class="akam-drawer__close" type="button" aria-label="Close" @click=${() => this.closeDrawer()}>
                  <cds-icon icon="close" size="m"></cds-icon>
                </button>
              </div>
            </header>
            <section class="akam-drawer__body">
              <slot name="body"></slot>
              <slot></slot>
            </section>
          </div>
          <footer class=${['akam-drawer__footer', this.hasFooterContent ? '' : 'is-empty'].filter(Boolean).join(' ')}>
            <slot name="footer" @slotchange=${this._handleFooterSlotChange}></slot>
          </footer>
        </aside>
      </div>
    `;
  }

  private _inlineStyle(): string {
    const horizontal = this.position === 'left' || this.position === 'right';
    const styles: string[] = [];
    if (horizontal && this.width != null) styles.push(`width:${this._resolveSize(this.width, 480)}`);
    if (!horizontal && this.height != null) styles.push(`height:${this._resolveSize(this.height, 480)}`);
    return styles.join(';');
  }

  private _resolveSize(value: string | number, defaultPx: number): string {
    if (value === '' || value === undefined || value === null) return '';
    if (typeof value === 'number') return `${value}px`;
    const trimmed = String(value).trim();
    if (/^(\d+)$/.test(trimmed)) return `${trimmed}px`;
    return trimmed || `${defaultPx}px`;
  }

  private _autoFocus() {
    if (!this.open || !this.panelEl) return;
    // Save previously focused only when opening
    if (!this.previouslyFocused) this.previouslyFocused = document.activeElement as HTMLElement | null;
    // Try focus first focusable, else panel itself
    const focusable = this._getFocusable();
    (focusable[0] || this.panelEl).focus({ preventScroll: true });
  }

  private _getFocusable(): HTMLElement[] {
    if (!this.panelEl) return [];
    return Array.from(this.panelEl.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
  }

  private _afterOpen() {
    if (!this.openedOnce) this.openedOnce = true;
    this._lockBodyScroll();
    // Defer event emission to mirror Angular macro task timing
    const id = window.setTimeout(() => {
      this.dispatchEvent(new CustomEvent('opened', { bubbles: true, composed: true }));
    }, 0);
    this.timeouts.push(id);
  }

  private _beforeClose() {
    this.dispatchEvent(new CustomEvent('before-close', { bubbles: true, composed: true }));
    this._unlockBodyScroll();
    this._restoreFocus();
  }

  private _afterClosed() {
    this.dispatchEvent(new CustomEvent('closed', { bubbles: true, composed: true }));
    this.previouslyFocused = undefined;
  }

  private _restoreFocus() {
    if (this.previouslyFocused) {
      try { this.previouslyFocused.focus({ preventScroll: true }); } catch {}
    }
  }

  private _lockBodyScroll() {
    try {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
    } catch {}
  }
  private _unlockBodyScroll() {
    try {
      document.body.style.overflow = '';
      document.body.style.height = '';
    } catch {}
  }
  private _clearTimeouts() { this.timeouts.forEach(id => clearTimeout(id)); this.timeouts = []; }

  private _emitOpenChange(next: boolean) {
    this.dispatchEvent(new CustomEvent('open-change', { detail: next, bubbles: true, composed: true }));
    this.open = next; // internal uncontrolled default; allows two-way if external sets open attribute manually
  }

  private onBackdropClick = (e: MouseEvent) => {
    if (!this.closeOnOverlayClick) return;
    const panel = this.panelEl;
    if (panel && panel.contains(e.target as Node)) return; // ignore clicks inside panel
    this.closeDrawer();
  };

  private _boundOnKeydown?: (e: KeyboardEvent) => void;
  private onKeydown(e: KeyboardEvent) {
    if (!this.open) return;
    if (e.key === 'Escape' && this.closeOnEsc) {
      e.preventDefault();
      this.closeDrawer();
      return;
    }
    if (e.key === 'Tab') {
      this._handleTabKey(e);
    }
  }

  private _handleTabKey(e: KeyboardEvent) {
    const focusable = this._getFocusable();
    if (!focusable.length) { e.preventDefault(); (this.panelEl as HTMLElement)?.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;
    if (e.shiftKey) {
      if (active === first || !this.panelEl?.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // Keep the footer border hidden when consumers choose inline actions instead of slotting footer content.
  // We watch the footer <slot> for changes, detect whether any real nodes (elements or non-empty text)
  // are projected, and toggle `hasFooterContent` accordingly. That state drives the `is-empty` footer
  // class so the divider shows up only when the consumer actually supplies footer actions.
  private _handleFooterSlotChange = () => {
    this.hasFooterContent = this._evaluateFooterSlot();
  };

  private _evaluateFooterSlot(): boolean {
    if (!this.footerSlot) return false;
    const assignedNodes = this.footerSlot.assignedNodes({ flatten: true });
    return assignedNodes.some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) return true;
      if (node.nodeType === Node.TEXT_NODE) return node.textContent?.trim() !== '';
      return false;
    });
  }
}

declare global { interface HTMLElementTagNameMap { 'cds-drawer': DrawerElement; } }
